// Brands: the merged catalog (Casinos.mt, LCB, Casinos.org.ph, AskGamblers)
// with the facts open catalogs state about each brand, and the two paid
// checks — Google Trends and Google Play — run per card for one market.
import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import {
  getRadarBrands, getRadarChecks, runRadarCheck,
  type CheckKind, type PlayResult, type RadarBrand, type RadarCheck, type TrendsResult,
} from '@/api/radar';
import {
  EMPTY_BRAND_FILTERS, FACT_LABELS, brandHaystack, brandOptions, downloadJson, factValues, formatFact,
  matchesBrand, fmtDate, safeHttps, type BrandFilters,
} from './logic';
import { LoadError, Loading, Pager, SelectInput, TextInput } from './controls';

const MARKETS = [
  ['GB', 'United Kingdom'], ['US', 'United States'], ['CA', 'Canada'], ['BR', 'Brazil'], ['AR', 'Argentina'],
  ['DE', 'Germany'], ['ES', 'Spain'], ['IN', 'India'], ['AU', 'Australia'], ['MX', 'Mexico'],
  ['ID', 'Indonesia'], ['KR', 'South Korea'], ['PH', 'Philippines'],
] as const;
const PERIODS = [['today 12-m', '12 months'], ['today 3-m', '3 months'], ['today 5-y', '5 years']] as const;
const PAGE_SIZE = 24;

const STATUS_TAG: Record<string, string> = {
  inferred: 'FROM URL', candidate: 'CANDIDATE', query_corrected: 'QUERY FIXED', query_review: 'QUERY IN REVIEW',
};

export function BrandsTab() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ['radar', 'brands'], queryFn: getRadarBrands, staleTime: Infinity });
  const checks = useQuery({ queryKey: ['radar', 'checks'], queryFn: getRadarChecks });
  const [filters, setFilters] = useState<BrandFilters>(EMPTY_BRAND_FILTERS);
  const [market, setMarket] = useState<string>('GB');
  const [period, setPeriod] = useState<string>('today 12-m');
  const [page, setPage] = useState(1);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  const gridRef = useRef<HTMLDivElement>(null);

  const brands = useMemo(() => data.data?.brands ?? [], [data.data]);
  const haystacks = useMemo(() => new Map(brands.map((b) => [b.id, brandHaystack(b)])), [brands]);
  const options = useMemo(() => ({
    source: brandOptions(brands, 'source'), operator: brandOptions(brands, 'operator'),
    products: brandOptions(brands, 'products'), licenses: brandOptions(brands, 'licenses'),
    country: brandOptions(brands, 'country'), currencies: brandOptions(brands, 'currencies'),
    deposit_methods: brandOptions(brands, 'deposit_methods'),
  }), [brands]);
  const filtered = useMemo(
    () => brands.filter((b) => matchesBrand(b, filters, haystacks.get(b.id))),
    [brands, haystacks, filters],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const set = <K extends keyof BrandFilters>(key: K, value: BrandFilters[K]) => {
    setFilters((f) => ({ ...f, [key]: value, ...(key === 'ratingSource' ? { rating: '' } : {}) }));
    setPage(1);
  };

  const check = useMutation({
    mutationFn: runRadarCheck,
    onMutate: () => setMessage('Fetching data from SerpApi…'),
    onSuccess: (r) => {
      setMessage(r.cached ? 'Showing a saved result; no SerpApi request was used.' : 'Result fetched and saved.');
      qc.invalidateQueries({ queryKey: ['radar', 'checks'] });
    },
    onError: (e: Error) => { setMessage(e.message); qc.invalidateQueries({ queryKey: ['radar', 'checks'] }); },
  });

  const latest = (kind: CheckKind, query: string): RadarCheck | undefined => (checks.data?.checks ?? []).find(
    (c) => c.kind === kind && c.country === market && c.query === query.trim() && (kind === 'play' || c.period === period),
  );

  if (data.isLoading) return <Loading what="the brand catalog" />;
  if (data.error) return <LoadError error={data.error} />;

  return (
    <div className="space-y-5">
      <section className="grid gap-4 lg:grid-cols-[1fr_auto]">
        <div className="bg-surface border border-border rounded-lg p-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_auto] items-end">
          <TextInput label="Search brands" type="search" value={filters.q} onChange={(v) => set('q', v)} placeholder="Name, category, operator…" />
          <SelectInput label="Check market" value={market} onChange={setMarket} options={MARKETS} />
          <SelectInput label="Trends period" value={period} onChange={setPeriod} options={PERIODS} />
          <Button variant="secondary" onClick={() => downloadJson('igaming-radar.json', {
            exported_at: new Date().toISOString(), brands: filtered, checks: checks.data?.checks ?? [], imports: data.data?.imports ?? [],
          })}>↓ Export JSON</Button>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4 min-w-[220px]">
          <p className="text-xs text-content-muted uppercase tracking-wide">SerpApi · this month (UTC)</p>
          <p className="text-2xl font-semibold mt-1">{checks.data ? `${checks.data.used} / ${checks.data.limit}` : '—'}</p>
          <p className="text-xs text-content-subtle mt-1">Shared team limit. Repeats within 24 h come from cache.</p>
          {checks.data && !checks.data.key_configured && <p className="text-xs text-warning mt-1">SerpApi key is not configured, so checks are unavailable.</p>}
        </div>
      </section>

      <details className="bg-surface border border-border rounded-lg p-4">
        <summary className="cursor-pointer font-medium text-content">Catalog sources</summary>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-content-muted">
              <tr><th className="py-2 pr-4">Source</th><th className="py-2 pr-4">Records</th><th className="py-2">Brands</th></tr>
            </thead>
            <tbody>
              {(data.data?.imports ?? []).map((r, i) => (
                <tr key={r.source_id} className="border-t border-border align-top">
                  <td className="py-2 pr-4">
                    <a href={safeHttps(r.url)} target="_blank" rel="noreferrer" className="text-primary hover:underline">{i + 1}. {r.label} ↗</a>
                    <p className="text-xs text-content-subtle mt-1 max-w-2xl">{r.coverage}</p>
                  </td>
                  <td className="py-2 pr-4">{r.records}</td>
                  <td className="py-2">{r.brands}{r.source_id === 'casinos-mt' ? ' · domains separately' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-content-subtle mt-3">
          Matches are merged by normalized name (case, spaces, punctuation, a trailing "Casino"); regional versions stay separate.
          These are candidates, not confirmed active operators. "FROM URL" names were taken from the review's address.
        </p>
      </details>

      <details open className="bg-surface border border-border rounded-lg p-4">
        <summary className="cursor-pointer font-medium text-content">Filters by collected details</summary>
        <div className="grid gap-3 mt-3 sm:grid-cols-2 lg:grid-cols-4">
          <SelectInput label="Coverage" value={filters.coverage} onChange={(v) => set('coverage', v)} all="All" options={[['yes', 'Has details'], ['no', 'Unknown so far']]} />
          <SelectInput label="Source" value={filters.source} onChange={(v) => set('source', v)} all="All" options={options.source} />
          <SelectInput label="Product type" value={filters.products} onChange={(v) => set('products', v)} all="All" options={options.products} />
          <SelectInput label="Operator" value={filters.operator} onChange={(v) => set('operator', v)} all="All" options={options.operator} />
          <SelectInput label="License (per catalog)" value={filters.licenses} onChange={(v) => set('licenses', v)} all="All" options={options.licenses} />
          <SelectInput label="Operating status" value={filters.status} onChange={(v) => set('status', v)} all="All" options={[['closed', 'Closed per source'], ['active', 'Active per source'], ['unknown', 'Unknown']]} />
          <TextInput label="Founded from" type="number" min={1900} max={2100} value={filters.year} onChange={(v) => set('year', v)} placeholder="e.g. 2025" />
          <SelectInput label="Market (per source)" value={filters.country} onChange={(v) => set('country', v)} all="All" options={options.country} />
          <SelectInput label="Currency" value={filters.currencies} onChange={(v) => set('currencies', v)} all="All" options={options.currencies} />
          <SelectInput label="Deposit method" value={filters.deposit_methods} onChange={(v) => set('deposit_methods', v)} all="All" options={options.deposit_methods} />
          <SelectInput label="Official domain" value={filters.website} onChange={(v) => set('website', v)} all="All" options={[['yes', 'Listed by a source'], ['no', 'Unknown']]} />
          <SelectInput label="Rating source" value={filters.ratingSource} onChange={(v) => set('ratingSource', v)} options={[['lcb', 'LCB · 0–5 scale'], ['askgamblers', 'AskGamblers · 0–10 scale']]} />
          <TextInput label="Rating from" type="number" min={0} max={filters.ratingSource === 'lcb' ? 5 : 10} step={0.1} value={filters.rating} onChange={(v) => set('rating', v)} placeholder="Any" />
          <label className="flex items-center gap-2 text-sm text-content-muted self-end pb-2">
            <input type="checkbox" checked={filters.newOnly} onChange={(e) => set('newOnly', e.target.checked)} />
            Found in AskGamblers New
          </label>
          <div className="self-end">
            <Button variant="secondary" onClick={() => { setFilters({ ...EMPTY_BRAND_FILTERS, q: filters.q }); setPage(1); }}>Reset filters</Button>
          </div>
        </div>
      </details>

      <div ref={gridRef} className="flex flex-wrap items-baseline justify-between gap-2 scroll-mt-4">
        <h2 className="font-medium text-content">Brand catalog <span className="text-content-subtle">{filtered.length}</span></h2>
        <span className="text-xs text-content-subtle">Checks run manually · 1 SerpApi request per click when not cached</span>
      </div>
      {message && <p role="status" className="text-sm text-content-muted">{message}</p>}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {visible.map((b) => {
          const query = drafts[b.id] ?? b.query;
          return (
            <BrandCard
              key={b.id}
              brand={b}
              query={query}
              onQuery={(v) => setDrafts((d) => ({ ...d, [b.id]: v }))}
              busy={check.isPending}
              canCheck={!!checks.data?.key_configured}
              onCheck={(kind) => check.mutate({ brand_id: b.id, kind, query, country: market, period })}
              trends={latest('trends', query)}
              play={latest('play', query)}
            />
          );
        })}
      </div>
      {!filtered.length && <p className="text-sm text-content-subtle">No brands found.</p>}
      <Pager page={current} pages={pages} note={`${PAGE_SIZE} per page`} onPage={(p) => { setPage(p); gridRef.current?.scrollIntoView({ behavior: 'smooth' }); }} />

      <section className="grid gap-4 md:grid-cols-2 text-sm">
        <div className="bg-surface border border-border rounded-lg p-4">
          <h3 className="font-medium text-content">Open sources</h3>
          <p className="text-content-muted mt-1">Being listed does not confirm a license or availability in a country.</p>
          <p className="mt-2 space-x-3">
            <a className="text-primary hover:underline" href="https://www.askgamblers.com/" target="_blank" rel="noreferrer">AskGamblers ↗</a>
            <a className="text-primary hover:underline" href="https://www.gamblingcommission.gov.uk/public-register/businesses/full" target="_blank" rel="noreferrer">UKGC ↗</a>
            <a className="text-primary hover:underline" href="https://www.mga.org.mt/licensee-hub/licensee-register/" target="_blank" rel="noreferrer">MGA ↗</a>
          </p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <h3 className="font-medium text-content">How to read the metrics</h3>
          <p className="text-content-muted mt-1">
            Trends Score is relative within a single query, not traffic; do not compare cards with each other.
            Google Play is the first page of results: a name match does not confirm an official app or a competitor.
          </p>
        </div>
      </section>
    </div>
  );
}

function BrandCard({ brand: b, query, onQuery, busy, canCheck, onCheck, trends, play }: {
  brand: RadarBrand; query: string; onQuery: (v: string) => void; busy: boolean; canCheck: boolean;
  onCheck: (kind: CheckKind) => void; trends?: RadarCheck; play?: RadarCheck;
}) {
  const year = factValues(b, 'founded_year')[0];
  const operator = factValues(b, 'operator')[0];
  const closed = factValues(b, 'operating_status')[0] === 'closed';
  const heading = [year ? `Founded: ${year}` : null, operator ? String(operator) : null, closed ? 'Closed per source' : null].filter(Boolean).join(' · ');
  const shown = b.facts.filter((f) => FACT_LABELS[f.field]);
  const fieldCount = new Set(shown.map((f) => f.field)).size;

  return (
    <article className="bg-surface border border-border rounded-lg p-4 flex flex-col gap-3 min-w-0">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 shrink-0 rounded-md bg-surface-muted text-primary flex items-center justify-center text-sm font-semibold">
          {b.name.slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-medium text-content break-words">{b.name}</h3>
          <p className="text-xs text-content-subtle">{b.category}</p>
        </div>
        <span className="text-[10px] font-semibold tracking-wide text-content-muted border border-border rounded px-1.5 py-0.5 shrink-0">{STATUS_TAG[b.status] ?? b.status}</span>
      </div>

      {b.source_url
        ? <a href={safeHttps(b.source_url)} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">{b.source_label} ↗</a>
        : <span className="text-sm text-content-muted">{b.source_label}</span>}

      {b.sources.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-content-muted">Sources and variants ({b.sources.length})</summary>
          <ul className="mt-1 space-y-1">
            {b.sources.map((s, i) => (
              <li key={i}>
                <a href={safeHttps(s.source_url)} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{s.source_id} · {s.raw_name} ↗</a>
                {s.domain && <span className="block text-xs text-content-subtle">{s.domain}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      {b.facts.length === 0 ? (
        <p className="text-xs text-content-subtle">No catalog details yet.</p>
      ) : (
        <div className="text-sm">
          <p className="text-content">{heading || 'Catalog details available'}</p>
          <details>
            <summary className="cursor-pointer text-content-muted">Profile · {fieldCount} fields</summary>
            <p className="text-xs text-content-subtle mt-1">Catalog data, not a regulator check. A missing field means "unknown".</p>
            <dl className="mt-2 space-y-2">
              {shown.map((f, i) => (
                <div key={i} className="border-t border-border pt-2">
                  <dt className="text-xs font-medium text-content-muted">{FACT_LABELS[f.field]}</dt>
                  <dd className="text-content break-words">{formatFact(f.value)}</dd>
                  <dd className="text-xs text-content-subtle">
                    <a href={safeHttps(f.source_url)} target="_blank" rel="noreferrer" className="text-primary hover:underline">{f.source_id} ↗</a>
                    {' · '}{fmtDate(f.observed_at)}
                  </dd>
                </div>
              ))}
            </dl>
          </details>
        </div>
      )}

      <div className="mt-auto space-y-2">
        <TextInput label="Query for Trends / Google Play" value={query} onChange={onQuery} />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy || !canCheck} onClick={() => onCheck('trends')}>↗ Check Trends</Button>
          <Button size="sm" variant="secondary" disabled={busy || !canCheck} onClick={() => onCheck('play')}>▷ Play competition</Button>
        </div>
        {trends && <TrendsBox check={trends} />}
        {play && <PlayBox check={play} />}
      </div>
    </article>
  );
}

function Stamp({ check }: { check: RadarCheck }) {
  return (
    <p className="text-xs text-content-subtle">
      {check.country} · {check.query} · {fmtDate(check.checked_at)}{check.admin_name ? ` · ${check.admin_name}` : ''}
    </p>
  );
}

function TrendsBox({ check }: { check: RadarCheck }) {
  const d = check.result as TrendsResult;
  const values = d.timeline.map((p) => p.value);
  const valid = values.filter((v): v is number => Number.isFinite(v));
  const avg = valid.length ? (valid.reduce((a, v) => a + v, 0) / valid.length).toFixed(1) : '—';
  const points = values.map((v, i) => `${(i * 300) / Math.max(1, values.length - 1)},${76 - (v ?? 0) * 0.7}`).join(' ');
  return (
    <div className="border border-border rounded-md p-3 text-sm space-y-1">
      <strong className="text-content">Trends Score</strong>
      <Stamp check={check} />
      {d.empty ? (
        <p className="text-content-muted">Not enough data, or no measurable interest.</p>
      ) : (
        <>
          <svg viewBox="0 0 300 80" className="w-full h-16 text-primary" role="img" aria-label="Trends Score over time">
            <polyline points={points} fill="none" stroke="currentColor" strokeWidth={2} />
          </svg>
          <p><span className="text-lg font-semibold text-content">{avg}</span> <span className="text-content-muted">/ 100 · period average</span></p>
        </>
      )}
      <p className="text-xs text-content-subtle">The query's own scale, not a comparison between brands.</p>
      <details>
        <summary className="cursor-pointer text-xs text-content-muted">Time series points ({values.length})</summary>
        <div className="text-xs text-content-muted max-h-48 overflow-y-auto">
          {d.timeline.map((p) => <div key={p.date}>{p.date}: {p.value ?? 'no data'}{p.partial ? ' · partial period' : ''}</div>)}
        </div>
      </details>
    </div>
  );
}

function PlayBox({ check }: { check: RadarCheck }) {
  const d = check.result as PlayResult;
  return (
    <div className="border border-border rounded-md p-3 text-sm space-y-1">
      <strong className="text-content">Google Play · {d.count} apps in response</strong>
      <Stamp check={check} />
      <p className="text-xs text-content-subtle">First page only, not the total number of competitors. Publisher and ownership are not verified.</p>
      {d.count ? d.apps.map((a) => (
        <div key={a.id} className="border-t border-border pt-1">
          <a href={safeHttps(a.link)} target="_blank" rel="noreferrer" className="text-primary hover:underline font-medium">{a.title} ↗</a>
          <p className="text-xs text-content-subtle">{a.author || 'Publisher not listed'} · ★ {a.rating ?? '—'} · {a.downloads ?? 'downloads not listed'}</p>
          <p className="text-xs text-content-subtle break-all">{a.id}</p>
        </div>
      )) : <p className="text-content-muted">No apps in the response. That does not mean there is no competition.</p>}
    </div>
  );
}
